import React, { useCallback, useState, memo, useRef, useImperativeHandle, forwardRef, PropsWithChildren } from 'react';

import useBlockBodyScroll from 'hooks/useBlockBodyScroll';
import { useSelector } from 'react-redux';
import { Column, Typography } from 'shared/Houston';
import { selectInnerHeight } from 'store/selectors';

import Cancel from '@eduzz/houston-icons/Cancel';

import { Overlay, ModalContainer, Header, Title, CloseIcon, Content, Footer } from './styles';
import useOnClickOut from './UseOnClickOut';

interface IProps {
  id: string;
  title: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  small?: boolean;
  footer?: React.ReactNode;
  onClickOut?: () => void;
}

export interface IModal {
  open: boolean;
  openModal: () => void;
  closeModal: () => void;
}

const Modal = forwardRef<IModal, PropsWithChildren<IProps>>(
  ({ children, title, subtitle, footer, small, onClickOut, id }, ref) => {
    const [open, setOpen] = useState(false);
    const innerHeight = useSelector(selectInnerHeight);

    const onOpen = useCallback(() => {
      setOpen(true);
    }, []);

    const onClose = useCallback(() => {
      setOpen(false);
    }, []);

    const refModal = useRef<IModal>({
      open,
      openModal: onOpen,
      closeModal: onClose
    });

    const refOnClickOut = useRef<HTMLDivElement>(null);
    useOnClickOut(refOnClickOut, onClickOut ?? onClose);

    useImperativeHandle(
      ref,
      (): IModal => ({
        open: refModal.current?.open,
        openModal: refModal.current?.openModal,
        closeModal: refModal.current.closeModal
      })
    );

    const containerVariant = {
      initial: { top: '-12%', transition: { type: 'spring' } },
      open: { top: '12%' },
      exit: { top: '-12%' }
    };

    useBlockBodyScroll(open);

    return (
      <>
        {open && (
          <Overlay maxHeight={innerHeight}>
            <ModalContainer id={id} variants={containerVariant} ref={refOnClickOut} small={!!small}>
              <Header>
                <Column>
                  {React.isValidElement(title) ? (
                    React.cloneElement(title)
                  ) : (
                    <Title size='md' weight='semibold'>
                      <span className='default'>{title}</span>
                    </Title>
                  )}
                  <Typography size='xs'>
                    {React.isValidElement(subtitle) ? (
                      React.cloneElement(subtitle)
                    ) : (
                      <span className='default'>{subtitle}</span>
                    )}
                  </Typography>
                </Column>
                <CloseIcon onClick={() => onClose()}>
                  <Cancel />
                </CloseIcon>
              </Header>
              <Content>{children}</Content>
              {React.isValidElement(footer) ? <Footer>{React.cloneElement(footer)}</Footer> : null}
            </ModalContainer>
          </Overlay>
        )}
      </>
    );
  }
);

export default memo(Modal);
